import "./AerialImagery.css";
import * as React from "react";
import { getMapView } from "../../../utils/arcgisUtils";
import Query from "@arcgis/core/rest/support/Query";
import ImageryLayer from "@arcgis/core/layers/ImageryLayer";
import Checkbox from "../../genericComponents/Checkbox/Checkbox";
import CheckedIcon from "../../../assets/layerlist/checked.svg?react";
import UncheckedIcon from "../../../assets/layerlist/unchecked.svg?react";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import _ from "lodash";
import type Geometry from "@arcgis/core/geometry/Geometry";

interface ImageryProject {
    name: string;
    year: number;
    photoDate: string;
    orthoType: string;
}

const AerialImagery = () => {
    console.log("Rendering AerialImagery component");
    const [layer, setLayer] = React.useState<ImageryLayer | null>(null);
    const [projects, setProjects] = React.useState<ImageryProject[] | null>(null);
    const [selectedProjects, setSelectedProjects] = React.useState<ImageryProject[]>([]);
    const [isRefreshing, setIsRefreshing] = React.useState<boolean>(false);

    const groupedByOrthoType = React.useMemo(() => {
        if (!projects) return null as Record<string, ImageryProject[]> | null;
        return _.groupBy(projects, (p) => p.orthoType || "Ukjent");
    }, [projects]);

    const toggleProject = (project: ImageryProject) => {
        setSelectedProjects((prevSelected) => {
            if (prevSelected.some((p) => p.name === project.name)) {
                return prevSelected.filter((p) => p.name !== project.name);
            } else {
                return [...prevSelected, project];
            }
        });
    };

    const fetchProjects = React.useCallback(
        (extent: Geometry) => {
            if (!layer) {
                return;
            }
            setProjects(null);

            const params = new Query({
                geometry: extent,
                where: "name=geomap_prosjektnavn AND src_prosjektnavn IS NOT NULL AND aar IS NOT NULL AND src_foto_date IS NOT NULL",
                outFields: ["src_prosjektnavn", "aar", "src_foto_date", "ortofototype"],
                returnGeometry: false,
                returnDistinctValues: true,
                orderByFields: [`src_foto_date DESC`],
            });

            layer.queryRasters(params).then((response) => {
                const projects = response.features.map((f) => {
                    return {
                        name: f.attributes.src_prosjektnavn,
                        year: f.attributes.aar,
                        photoDate: f.attributes.src_foto_date,
                        orthoType: f.attributes.ortofototype === 6 ? "Satellittbilder" : "Flybilder",
                    } as ImageryProject;
                });
                const uniqueProjects = projects.filter((p, i, arr) => arr.findIndex((x) => x.name === p.name) === i);
                setProjects(uniqueProjects);
                setSelectedProjects((prev) => prev.filter((p) => uniqueProjects.some((up) => up.name === p.name)));
            });
        },
        [layer]
    );

    const debounceExtentChange = React.useMemo<_.DebouncedFunc<(extent: Geometry) => void>>(
        () =>
            _.debounce((extent: Geometry) => {
                fetchProjects(extent);
            }, 200),
        [fetchProjects]
    );

    React.useEffect(() => {
        if (!layer) {
            return;
        }

        if (selectedProjects?.length > 0) {
            const projectsString = selectedProjects.map((p) => {
                return `'${p.name}'`;
            });
            const defExpression = `src_prosjektnavn IN (${projectsString})`;
            layer.set({ definitionExpression: defExpression });
            layer.set({ visible: true });
        } else {
            layer.set({ definitionExpression: "1=0" });
            layer.set({ visible: false });
        }
    }, [selectedProjects, layer]);

    React.useEffect(() => {
        const mapView = getMapView();
        if (!mapView?.map) return;

        fetchProjects(mapView.extent);
    }, [layer, fetchProjects]);

    const title = "Historiske flyfoto";

    React.useEffect(() => {
        const mapView = getMapView();
        if (!mapView?.map) return;

        const existingLayer = mapView.map.allLayers.find((lyr) => lyr.title === title) as ImageryLayer;
        if (existingLayer) {
            setLayer(existingLayer);
            return;
        }

        const layer = new ImageryLayer({
            url: "https://maps.kystverket.cloudgis.no/enterprise/sharing/servers/410b04072de3446dbbbb751a86feeebd/rest/services/Geomap_UTM33_EUREF89/GeomapBilder3/ImageServer",
            title: title,
            visible: false,
        });
        mapView.map.add(layer, 0);
        setLayer(layer);
    }, []);

    React.useEffect(() => {
        const mapView = getMapView();
        if (!mapView?.map || !layer) return;

        const handle = reactiveUtils.watch(
            () => mapView.extent,
            (extent) => {
                debounceExtentChange(extent);
            }
        );

        return () => {
            handle.remove();
        };
    }, [layer, debounceExtentChange]);

    React.useEffect(() => {
        const mapView = getMapView();
        if (!mapView?.map || !layer) return;

        let removed = false;
        let handle: { remove: () => void } | null = null;

        mapView.whenLayerView(layer).then((layerView) => {
            if (removed) return;
            // Initialize with current state
            setIsRefreshing(!!layerView.updating);
            handle = reactiveUtils.watch(
                () => layerView.updating,
                (updating) => setIsRefreshing(!!updating)
            );
        });

        return () => {
            removed = true;
            handle?.remove();
        };
    }, [layer]);

    React.useEffect(() => {
        return () => {
            // cancel any pending debounced calls on unmount or change
            debounceExtentChange.cancel();
        };
    }, [debounceExtentChange]);

    return (
        <div className="aerial-imagery-container">
            {!projects && <p>Laster...</p>}
            {projects && projects.length === 0 && <p>Det ikke finnes noe flyfoto for dette området.</p>}
            {projects && isRefreshing && (
                <div className="aerial-imagery-refresh-sticky" aria-hidden="true">
                    <div className="aerial-imagery-refresh" aria-live="polite">
                        Oppdaterer...
                    </div>
                </div>
            )}
            {groupedByOrthoType &&
                Object.entries(groupedByOrthoType)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([type, list]) => (
                        <div key={type} style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 600, margin: "8px 0" }}>{type}</div>
                            {list
                                .slice()
                                .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
                                .map((project) => (
                                    <div key={project.name} className="layerlist-details-layer">
                                        <Checkbox
                                            checked={selectedProjects.some((p) => p.name === project.name)}
                                            checkedIcon={CheckedIcon}
                                            uncheckedIcon={UncheckedIcon}
                                            id={`${type}-${project.name}`}
                                            onChange={() => {
                                                toggleProject(project);
                                            }}
                                        />
                                        <label className="w-100" htmlFor={`${type}-${project.name}`}>
                                            {project.name}
                                        </label>
                                    </div>
                                ))}
                        </div>
                    ))}
        </div>
    );
};

export default AerialImagery;
